import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Badge, Button, FormField, Select, Spinner, Text, TextInput } from '@kui/foundations-react';
import { HorizontalLogo } from '@kui/foundations-react/Logo';
import * as ApiMethods from '../constants/apiMethods';
import '../styles/RegCard.css';
import '../styles/TenantPicker.css';

// Replaces ClaimEnablement (Figma "Tenant Picker"). Same props, same API call, same onComplete body,
// same redirect to NCA for "Create new" - only the UI and the wording ("account" -> "tenant") changed.

const ALL = 'all';
const OWNER = 'owner';
const MEMBER = 'member';
const FILTER_ITEMS = [
  { value: ALL, children: 'All Tenants' },
  { value: OWNER, children: 'Tenant Owner' },
  { value: MEMBER, children: 'Tenant Member' },
];

const formatSubscription = (value) => {
  if (!value) return '-';
  if (value === 'NVIDIA_AI_ENTERPRISE_VIEWER') {
    return value
      .split('_')
      .map((word, index) => (index <= 1 ? word : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()))
      .join(' ');
  }
  if (value.toLowerCase() === 'nv-ai-enterprise') return 'NVIDIA AI Enterprise';
  return value
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export default function TenantPicker({ productActivationId, user, accountsData: initialAccountsData, onComplete }) {
  const [filter, setFilter] = useState(ALL);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [accountsData, setAccountsData] = useState(initialAccountsData || null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // created once; the same value is sent to NCA and kept in sessionStorage
  const [nonce] = useState(() => (typeof window !== 'undefined' ? crypto.randomUUID() : ''));
  const userEmail = user && user.email ? user.email : localStorage.getItem('email') || '';

  useEffect(() => {
    try {
      window.sessionStorage.setItem('nonce', nonce);
    } catch (e) {
      // storage unavailable - nothing to do
    }
  }, [nonce]);

  useEffect(() => {
    const fetchAccountsData = async () => {
      try {
        setLoading(true);
        setLoadFailed(false);
        const queryParams = new URLSearchParams();
        if (user.ngc_ProductFam) {
          queryParams.append('ngc_ProductFam', user.ngc_ProductFam);
        }
        queryParams.append(
          'ngcProductNames',
          user.ngcProductNames && user.ngcProductNames !== 'undefined' ? user.ngcProductNames : ''
        );
        queryParams.append('isMultiEnabled', user.isMultiEnabled ? user.isMultiEnabled : false);
        const apiUrl = `${ApiMethods.GET_NGCAccounts}?${queryParams.toString()}`;

        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: `Bearer ${user.token}`,
            'X-User-Sub': user.sub,
            'Content-Type': 'application/json',
          },
        });
        setAccountsData(response.data);
      } catch (error) {
        console.error('Failed to fetch tenants:', error && error.message);
        setLoadFailed(true);
      } finally {
        setLoading(false);
      }
    };

    if (productActivationId && !initialAccountsData) {
      fetchAccountsData();
    } else {
      // nothing to fetch (or data handed in): do not leave the spinner up forever
      setAccountsData(initialAccountsData || null);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productActivationId, initialAccountsData, user.token, user.sub]);

  const accounts = accountsData && Array.isArray(accountsData.accounts) ? accountsData.accounts : [];
  const products = accountsData && Array.isArray(accountsData.products) ? accountsData.products : [];
  const productNames = products.map((p) => p && p.displayName).filter(Boolean).join(', ');
  const includedSubscriptions = [
    ...new Set(products.reduce((subs, product) => subs.concat(product.subscriptions || []), [])),
  ];

  const term = searchTerm.trim().toLowerCase();
  const matches = (account) =>
    !term ||
    (account.accountDisplayName || '').toLowerCase().includes(term) ||
    (account.orgDisplayName || '').toLowerCase().includes(term);
  const owners = filter === MEMBER ? [] : accounts.filter((a) => a.eligible && matches(a));
  const members = filter === OWNER ? [] : accounts.filter((a) => !a.eligible && matches(a));
  const nothingToShow = owners.length === 0 && members.length === 0;

  const selectedAccount = accounts.find((a) => a.accountId === selectedAccountId);
  const canContinue = !!selectedAccount && !!selectedAccount.eligible && !submitting;

  const handleContinue = () => {
    if (!canContinue) return;
    setSubmitting(true);
    onComplete({
      accountId: selectedAccountId,
      productActivationId,
      orgName: selectedAccount.orgName || '',
      accountNumber: selectedAccount.accountNumber || '',
      accountName: selectedAccount.accountName || '',
      ngcProductNames: user.ngcProductNames,
    });
  };

  const handleCreateNew = () => {
    localStorage.setItem('returnUrl', window.location.href);
    localStorage.setItem('NewAccountCreated', false);

    const callbackUrl = process.env.REACT_APP_BASE_NCA_Redirect_URI;
    const authurl = process.env.REACT_APP_BASE_NCA_CREATE_URL;
    if (!authurl) {
      console.error('REACT_APP_BASE_NCA_CREATE_URL is not defined');
      alert('Configuration error: REACT_APP_BASE_NCA_CREATE_URL is not defined');
      return;
    }
    if (!callbackUrl) {
      console.error('REACT_APP_BASE_NCA_Redirect_URI is not defined');
      alert('Configuration error: REACT_APP_BASE_NCA_Redirect_URI is not defined');
      return;
    }
    const qParams = new URLSearchParams({
      callbackUrl: callbackUrl || '',
      nonce: nonce || '',
      simplify: true,
      login_hint: userEmail || '',
    });
    try {
      window.location.assign(`${authurl}?${qParams}`);
    } catch (e) {
      console.error('Error redirecting to NCA:', e);
      localStorage.setItem('IsUserLoggedIn', 'false');
    }
  };

  const renderRow = (account) => {
    const selected = selectedAccountId === account.accountId;
    const disabled = !account.eligible;
    const subs = Array.isArray(account.subscriptions) ? account.subscriptions : [];
    return (
      <li key={account.accountId} role="presentation">
        <button
          type="button"
          role="radio"
          aria-checked={selected}
          aria-disabled={disabled}
          disabled={disabled}
          title={disabled ? account.ineligibilityReason || 'This tenant is not eligible.' : undefined}
          className={`tenantRow${selected ? ' tenantRow--selected' : ''}`}
          onClick={() => setSelectedAccountId(account.accountId)}
        >
          <span className="tenantRowName">
            <Text kind="body/regular/md" asChild>
              <span>{account.accountDisplayName || '—'}</span>
            </Text>
            {account.orgDisplayName ? (
              <Text kind="label/regular/md" asChild>
                <span className="tenantRowOrg">{account.orgDisplayName}</span>
              </Text>
            ) : null}
          </span>
          <span className="tenantRowSubs">
            {subs.map((sub, i) => (
              <Badge key={`${account.accountId}-${i}`} color="green" kind="outline" size="small">
                {formatSubscription(sub)}
              </Badge>
            ))}
          </span>
        </button>
      </li>
    );
  };

  const renderGroup = (label, list) =>
    list.length === 0 ? null : (
      <li role="presentation" key={label}>
        <Text kind="label/bold/md" asChild>
          <p className="tenantGroupLabel">{label}</p>
        </Text>
        <ul className="tenantGroupList" role="presentation">
          {list.map(renderRow)}
        </ul>
      </li>
    );

  return (
    <div className="regPage regPage--narrow">
      <div className="login-container">
        <div className="regCard regCard--signin tenantCard">
          <div className="regHeaderLogo">
            <HorizontalLogo role="img" aria-label="NVIDIA" />
          </div>

          {loading ? (
            <div className="tenantCenter">
              <Spinner slotDescription="Loading tenants" />
            </div>
          ) : (
            <>
              {(productNames || includedSubscriptions.length > 0) && (
                <section className="tenantProducts" aria-label="Product details">
                  <Text kind="label/bold/md" asChild>
                    <p>Product Details</p>
                  </Text>
                  <Text kind="body/regular/md" asChild>
                    <p className="tenantProductNames">{productNames || '—'}</p>
                  </Text>
                  <Text kind="label/bold/md" asChild>
                    <p>Included Subscriptions</p>
                  </Text>
                  <div className="tenantSubs">
                    {includedSubscriptions.length > 0 ? (
                      includedSubscriptions.map((sub, i) => (
                        <Badge key={`${sub}-${i}`} className="tenantSubBadge" color="green" kind="solid">
                          {formatSubscription(sub)}
                        </Badge>
                      ))
                    ) : (
                      <Text kind="label/regular/md" asChild>
                        <span>No subscriptions</span>
                      </Text>
                    )}
                  </div>
                </section>
              )}

              <Text kind="title/md" asChild>
                <h1 className="regHeaderTitle">Select a Tenant</h1>
              </Text>

              <Text kind="body/regular/md" asChild>
                <p className="tenantIntro">
                  Tenants manage organizational units that contain teams and users. Choose a tenant you own to
                  activate the product, or create a new tenant. For tenants you are a member of, contact the
                  tenant owner to activate the product.
                </p>
              </Text>

              {loadFailed && (
                <p role="alert" className="tenantError">
                  We could not load your tenants. You can try again later or create a new tenant.
                </p>
              )}

              {accounts.length > 0 && (
                <>
                  <div className="tenantToolbar">
                    <FormField id="tenantSearch" slotLabel="Search tenants or teams" labelPosition="top">
                      <TextInput
                        size="medium"
                        type="search"
                        placeholder="Search tenants or teams"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </FormField>
                    <FormField id="tenantFilter" slotLabel="Show" labelPosition="top">
                      <Select size="medium" items={FILTER_ITEMS} value={filter} onValueChange={setFilter} />
                    </FormField>
                  </div>

                  <div className="tenantListBox">
                    {nothingToShow ? (
                      <div className="tenantCenter tenantEmpty">
                        <Text kind="title/xs" asChild>
                          <p>No Results Found</p>
                        </Text>
                      </div>
                    ) : (
                      <ul role="radiogroup" aria-label="Tenants" className="tenantList">
                        {renderGroup('Tenant Owner', owners)}
                        {renderGroup('Tenant Member', members)}
                      </ul>
                    )}
                  </div>
                </>
              )}

              {accounts.length === 0 && !loadFailed && (
                <Text kind="body/regular/md" asChild>
                  <p className="tenantIntro">
                    You do not have any existing tenants. Create a new tenant to activate your product.
                  </p>
                </Text>
              )}

              <div className="tenantActions">
                <Button kind="secondary" color="neutral" onClick={handleCreateNew}>
                  Create new tenant
                </Button>
                {accounts.length > 0 && (
                  <Button kind="primary" color="brand" disabled={!canContinue} onClick={handleContinue}>
                    Continue
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
